import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import WidgetWrapper from './WidgetWrapper';
import { reports } from '../../services/api';

const RevenueWidget = ({ darkMode = false, onError }) => {
  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobile = windowWidth < 800;
  const chartHeight = isMobile ? 200 : 300;
  
  const [revenueData, setRevenueData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        const startDate = sixMonthsAgo.toISOString().split('T')[0];
        const endDate = now.toISOString().split('T')[0];
        
        const monthlyRevenue = await reports.revenue({ startDate, endDate, groupBy: 'month' });
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        
        const revenueResults = (monthlyRevenue.data || []).map(r => ({
          month: r.period ? monthNames[parseInt(r.period.split('-')[1]) - 1] : 'N/A',
          revenue: r.revenue || 0
        }));
        
        // Fill in missing months with $0 to always show trailing 6 months
        const resultMap = {};
        revenueResults.forEach(r => { resultMap[r.month] = r.revenue; });
        const finalResults = [];
        for (let i = 5; i >= 0; i--) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const m = monthNames[d.getMonth()];
          finalResults.push({ month: m, revenue: resultMap[m] || 0 });
        }
        setRevenueData(finalResults);
      } catch (error) {
        console.error('RevenueWidget error:', error);
        onError?.('RevenueWidget', error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [onError]);

  const subTextColor = darkMode ? '#9ca3af' : '#6b7280';
  const borderColor = darkMode ? '#374151' : '#e5e7eb';

  return (
    <WidgetWrapper title="💰 Revenue (Trailing 6 Months)" loading={loading} darkMode={darkMode}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <AreaChart data={revenueData}>
          <defs>
            <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
          <XAxis dataKey="month" fontSize={12} stroke={subTextColor} />
          <YAxis tickFormatter={(value) => `$${value / 1000}k`} fontSize={12} stroke={subTextColor} />
          <Tooltip 
            formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']} 
            contentStyle={{ 
              background: darkMode ? '#1f2937' : '#fff', 
              border: `1px solid ${borderColor}` 
            }} 
          />
          <Area type="monotone" dataKey="revenue" stroke="#2563eb" fill="url(#colorRevenue)" />
        </AreaChart>
      </ResponsiveContainer>
    </WidgetWrapper>
  );
};

// Widget metadata
RevenueWidget.widgetId = 'revenue';
RevenueWidget.title = '💰 Revenue (Trailing 6 Months)';
RevenueWidget.defaultSize = { cols: 6 };
RevenueWidget.category = 'business';

export default RevenueWidget;